import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  HelpCircle, 
  Search, 
  ChevronDown, 
  ChevronUp,
  Trophy,
  Users,
  Clock,
  Shield,
  Smartphone,
  Database,
  Mail,
  Phone,
  Globe
} from 'lucide-react';

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqCategories = [
    {
      title: "Getting Started",
      icon: <Trophy className="h-5 w-5 text-primary" />,
      questions: [
        {
          question: "How do I create my first tournament?",
          answer: "Navigate to the Tournaments page and click 'New Tournament'. Follow the setup wizard to configure your tournament details, including teams, format, and schedule. You can also import existing team data if available."
        },
        {
          question: "What information do I need to set up a team?",
          answer: "To create a team, you'll need the team name, captain details, and player information including names, roles (batsman, bowler, all-rounder), and contact details. You can add players individually or bulk import them."
        },
        {
          question: "Can I use the platform for free?",
          answer: "Yes! We offer a free tier with basic features including tournament creation, match scoring, and basic statistics. Premium features like advanced analytics, unlimited tournaments, and priority support are available with paid plans."
        },
        {
          question: "How do I invite team members to join?",
          answer: "Once you've created a team, you can invite players by sharing the team link or sending email invitations through the platform. Players can then register and join your team."
        }
      ]
    },
    {
      title: "Match Scoring",
      icon: <Clock className="h-5 w-5 text-primary" />,
      questions: [
        {
          question: "How do I start scoring a match?",
          answer: "Go to the Matches page, select your match, and click 'Start Scoring'. You'll need to set up the teams, toss, and initial batting order. The scoring interface will then be available for ball-by-ball updates."
        },
        {
          question: "Can I edit scores after entering them?",
          answer: "Yes, you can edit recent ball entries within the same over. For older entries, you may need to contact an administrator. We recommend double-checking scores before confirming each ball."
        },
        {
          question: "How do I handle extras like wides and no-balls?",
          answer: "Use the extras button in the scoring interface. Select the type of extra (wide, no-ball, bye, leg-bye) and add any additional runs. The system will automatically calculate the total runs for that ball."
        },
        {
          question: "What happens if I lose internet connection during scoring?",
          answer: "The platform saves data locally and will sync when your connection is restored. However, we recommend having a backup scorer or using the mobile app for offline scoring capabilities."
        }
      ]
    },
    {
      title: "Account & Security",
      icon: <Shield className="h-5 w-5 text-primary" />,
      questions: [
        {
          question: "How do I reset my password?",
          answer: "Click 'Forgot Password' on the login page and enter your email address. You'll receive a password reset link. Make sure to check your spam folder if you don't see the email."
        },
        {
          question: "Can I change my email address?",
          answer: "Yes, you can update your email address in your account settings. You'll need to verify the new email address before the change takes effect."
        },
        {
          question: "Is my cricket data secure?",
          answer: "Yes, we use industry-standard encryption and security measures to protect your data. All data is encrypted in transit and at rest. We also conduct regular security audits."
        },
        {
          question: "Can I export my data?",
          answer: "Yes, you can export your tournament data, match statistics, and player records in various formats including CSV and PDF. This feature is available in your account settings."
        }
      ]
    },
    {
      title: "Technical Support",
      icon: <HelpCircle className="h-5 w-5 text-primary" />,
      questions: [
        {
          question: "What browsers are supported?",
          answer: "We support all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, we recommend using the latest version of Chrome or Firefox."
        },
        {
          question: "Does the platform work on mobile devices?",
          answer: "Yes! Our platform is fully responsive and works on smartphones and tablets. We also offer a mobile-optimized interface for scoring matches on the go."
        },
        {
          question: "How do I report a bug or issue?",
          answer: "You can report issues through the Contact page, email us at support@cricketscoring.com, or use the feedback form in the app. Please include details about the issue and your device/browser information."
        },
        {
          question: "What if I can't access my account?",
          answer: "First, try resetting your password. If that doesn't work, contact our support team with your email address and we'll help you regain access to your account."
        }
      ]
    },
    {
      title: "Data & Statistics",
      icon: <Database className="h-5 w-5 text-primary" />,
      questions: [
        {
          question: "How accurate are the statistics?",
          answer: "Statistics are calculated based on the data you input. We provide tools to ensure accuracy, but we recommend cross-checking important statistics with official sources when possible."
        },
        {
          question: "Can I import data from other platforms?",
          answer: "Yes, we support importing data from CSV files and some other cricket platforms. Contact our support team for specific import formats and assistance."
        },
        {
          question: "How long is my data stored?",
          answer: "Your data is stored indefinitely unless you request deletion. We maintain backups to ensure data safety and availability."
        },
        {
          question: "Can I share statistics publicly?",
          answer: "Yes, you can share tournament and match statistics through public links or social media. You control the privacy settings for your tournaments and matches."
        }
      ]
    },
    {
      title: "Billing & Subscriptions",
      icon: <Globe className="h-5 w-5 text-primary" />,
      questions: [
        {
          question: "What payment methods do you accept?",
          answer: "We accept major credit cards, debit cards, and PayPal. All payments are processed securely through our payment partners."
        },
        {
          question: "Can I cancel my subscription anytime?",
          answer: "Yes, you can cancel your subscription at any time through your account settings. You'll continue to have access until the end of your current billing period."
        },
        {
          question: "Do you offer refunds?",
          answer: "We offer a 30-day money-back guarantee for new subscriptions. If you're not satisfied, contact our support team within 30 days of your first payment."
        },
        {
          question: "Are there any hidden fees?",
          answer: "No hidden fees. The price you see is what you pay. We're transparent about all costs and will notify you of any changes to pricing in advance."
        }
      ]
    }
  ];

  const allQuestions = faqCategories.flatMap((category, categoryIndex) =>
    category.questions.map((item, questionIndex) => ({
      ...item,
      category: category.title,
      categoryIcon: category.icon,
      globalIndex: categoryIndex * 100 + questionIndex
    }))
  );

  const filteredQuestions = searchTerm
    ? allQuestions.filter(q => 
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allQuestions;

  const popularQuestions = [
    "How do I create my first tournament?",
    "Can I use the platform for free?",
    "How do I start scoring a match?",
    "What browsers are supported?",
    "How do I reset my password?"
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-muted-foreground mb-6">
            Find answers to common questions about our cricket scoring platform.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Popular Questions */}
          {!searchTerm && (
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {popularQuestions.map((question, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm(question)}
                  className="text-xs"
                >
                  {question}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Search Results or Categories */}
        {searchTerm ? (
          // Search Results
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Search Results
              </h2>
              <p className="text-muted-foreground">
                Found {filteredQuestions.length} result{filteredQuestions.length !== 1 ? 's' : ''} for "{searchTerm}"
              </p>
            </div>
            
            {filteredQuestions.length > 0 ? (
              <div className="space-y-4">
                {filteredQuestions.map((item, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          {item.categoryIcon}
                          <div>
                            <CardTitle className="text-lg cursor-pointer" onClick={() => toggleItem(item.globalIndex)}>
                              {item.question}
                            </CardTitle>
                            <Badge variant="secondary" className="text-xs">
                              {item.category}
                            </Badge>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleItem(item.globalIndex)}
                        >
                          {expandedItems.includes(item.globalIndex) ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </CardHeader>
                    {expandedItems.includes(item.globalIndex) && (
                      <CardContent>
                        <p className="text-muted-foreground">{item.answer}</p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No results found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try searching with different keywords or browse our categories below.
                  </p>
                  <Button onClick={() => setSearchTerm('')}>
                    Clear Search
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          // Categories
          <div className="space-y-8">
            {faqCategories.map((category, categoryIndex) => (
              <Card key={categoryIndex} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    {category.icon}
                    <CardTitle className="text-xl">{category.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {category.questions.map((item, questionIndex) => {
                      const globalIndex = categoryIndex * 100 + questionIndex;
                      return (
                        <div key={questionIndex} className="border-b border-border last:border-b-0 pb-4 last:pb-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 
                                className="font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
                                onClick={() => toggleItem(globalIndex)}
                              >
                                {item.question}
                              </h3>
                              {expandedItems.includes(globalIndex) && (
                                <p className="text-muted-foreground mt-2">{item.answer}</p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleItem(globalIndex)}
                              className="ml-4 flex-shrink-0"
                            >
                              {expandedItems.includes(globalIndex) ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Contact Support */}
        <Card className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-bold text-foreground mb-4">
              Still Have Questions?
            </h2>
            <p className="text-muted-foreground mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-primary text-primary-foreground">
                <Mail className="h-4 w-4 mr-2" />
                Contact Support
              </Button>
              <Button variant="outline">
                <Phone className="h-4 w-4 mr-2" />
                Call Us
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FAQPage; 